import React from "react";
import { MapPin, Lock, Diamond, Palette } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { useAuth } from "@/context/AuthContext";
import { CARAT_BUCKETS } from "@/i18n/translations";

const TINT = {
  sapphire: { text: "#6B9EE8", glow: "rgba(15, 82, 186, 0.18)", border: "rgba(15, 82, 186, 0.28)" },
  ruby:     { text: "#E86B8C", glow: "rgba(224, 17, 95, 0.16)", border: "rgba(224, 17, 95, 0.30)" },
  emerald:  { text: "#6BE895", glow: "rgba(80, 200, 120, 0.16)", border: "rgba(80, 200, 120, 0.28)" },
};

const HAS_COLOR_CHART = { sapphire: true, ruby: true, emerald: true };

export const GemCard = ({ gemType, imgSrc, onOpenSubscribe, onOpenFiche, onOpenPicker, onOpenColorChart }) => {
  const { t, lang } = useLang();
  const { subscribed } = useSubscription();
  const { user } = useAuth();
  const isSubscribed = subscribed || user?.subscription_active;
  const tint = TINT[gemType];
  const title = t.gems[gemType];
  const origins = t.origins[gemType];
  const hasColorChart = HAS_COLOR_CHART[gemType];

  return (
    <article
      data-testid={`gem-card-${gemType}`}
      className="relative flex flex-col rounded-sm bg-[#080C17] border border-[#C1A661]/15 overflow-hidden"
      style={{ boxShadow: `inset 0 0 80px ${tint.glow}` }}
    >
      {/* Top: image + title */}
      <div className="relative flex items-center gap-4 p-6 pb-4 border-b border-[#C1A661]/10">
        <div className="relative w-28 h-28 shrink-0">
          <div
            className="absolute inset-0 rounded-full blur-2xl opacity-70"
            style={{ background: `radial-gradient(circle, ${tint.glow} 0%, transparent 70%)` }}
          />
          <img
            src={imgSrc}
            alt={title}
            className="relative w-28 h-28 object-cover rounded-sm"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-serif font-light tracking-wider text-2xl md:text-3xl text-[#F8F5EE] leading-tight">
            {title}
          </h2>
          <p className="mt-1 text-sm italic font-light" style={{ color: tint.text }}>
            {t.allOrigins}
          </p>
        </div>
      </div>

      {/* Origins */}
      <div className="px-6 pt-5">
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#C1A661] mb-3">
          {t.factSheets}
        </p>
        <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[13px] text-[#D8DBE2] font-light">
          {origins.map((o) => (
            <li key={o}>
              <button
                data-testid={`origin-${gemType}-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                onClick={() => onOpenFiche && onOpenFiche(o)}
                className="flex items-center gap-2 w-full text-left hover:text-[#F8F5EE] transition-colors group"
              >
                <MapPin size={11} strokeWidth={1.5} style={{ color: tint.text }} />
                <span className="group-hover:underline underline-offset-4 decoration-[#C1A661]/40">{o}</span>
              </button>
            </li>
          ))}
          <li className="col-span-2 mt-1 italic text-[#8A8F99] text-[12px]">{t.andOthers}</li>
        </ul>

        {hasColorChart && (
          <button
            type="button"
            data-testid={`color-chart-btn-${gemType}`}
            onClick={onOpenColorChart}
            className="mt-4 w-full flex items-center justify-center gap-2 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] bg-[#080C17]/60 hover:bg-[#080C17] px-4 py-2.5 transition-colors"
          >
            <Palette size={14} strokeWidth={1.5} className="text-[#C1A661]" />
            <span className="text-[10px] tracking-[0.25em] uppercase text-[#E2CFA4] font-medium">
              {lang === "fr" ? "Nuancier (accès libre)" : "Color chart (free access)"}
            </span>
          </button>
        )}
      </div>

      {/* Pricing grid */}
      <div className="px-6 pt-6">
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#C1A661] mb-3 text-center">
          {t.pricingGrid}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 border border-[#C1A661]/20 rounded-sm overflow-hidden">
          {CARAT_BUCKETS.map((b, i) => (
            <div
              key={b}
              className={`flex flex-col items-center justify-center gap-2 py-3.5 px-1 text-center bg-[#050811] min-h-[52px] ${
                i < CARAT_BUCKETS.length - 1 ? "border-r border-[#C1A661]/15" : ""
              }`}
            >
              <span className="text-[11px] text-[#A1A7B3] leading-tight font-light">{b}</span>
              {isSubscribed ? (
                <Diamond size={14} strokeWidth={1.4} className="text-[#C1A661]" />
              ) : (
                <Lock size={14} strokeWidth={1.4} className="text-[#C1A661]/70" />
              )}
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-[#8A8F99] font-light italic text-center leading-relaxed">
          {t.pricingNote}
        </p>
      </div>

      {/* CTA */}
      <div className="px-6 pt-5 pb-6 mt-auto">
        {isSubscribed ? (
          <button
            data-testid={`gem-picker-${gemType}`}
            onClick={onOpenPicker}
            className="w-full rounded-sm border py-3 px-4 flex items-center justify-center gap-3 hover:brightness-125 transition duration-200 group"
            style={{ borderColor: tint.border, backgroundColor: "rgba(5,8,17,0.7)" }}
          >
            <Diamond size={13} strokeWidth={1.5} className="text-[#C1A661]" />
            <div className="text-center leading-tight">
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#E2CFA4] font-medium">
                {t.viewDetailedPricing}
              </div>
              <div className="text-[10px] tracking-[0.18em] mt-0.5" style={{ color: tint.text }}>
                {t.selectCountryHint}
              </div>
            </div>
          </button>
        ) : (
          <button
            data-testid={`gem-cta-${gemType}`}
            onClick={onOpenPicker}
            className="w-full rounded-sm border py-3 px-4 flex items-center justify-center gap-3 hover:brightness-125 transition duration-200 group"
            style={{ borderColor: tint.border, backgroundColor: "rgba(5,8,17,0.7)" }}
          >
            <Lock size={13} strokeWidth={1.5} className="text-[#C1A661]" />
            <div className="text-center leading-tight">
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#E2CFA4] font-medium">
                {t.detailedPricing}
              </div>
              <div className="text-[10px] tracking-[0.18em] uppercase mt-0.5" style={{ color: tint.text }}>
                {t.availableAfter}
              </div>
            </div>
          </button>
        )}
      </div>
    </article>
  );
};
